import { del, get, post, put } from 'aws-amplify/api';
import { AdminDoc, AdminDocStatus } from '../models/admin-doc.model';
import { CreateAdminDocRequest, CreateAdminDocRequestSchema } from '../models/createAdminDoc.model';
import { replaceNullsWithEmpty } from '../utils/utils';

export async function getAdminDocList(filters?: {
  limit?: number;
  lastKey?: string;
  division?: string;
}): Promise<{ items: AdminDoc[]; lastKey?: string }> {
  const limit = filters?.limit ?? 100;
  const lastKey = filters?.lastKey ?? '';
  const division = filters?.division;

  let path = `/admin-docs?limit=${limit}`;
  if (lastKey) path += `&lastKey=${lastKey}`;
  if (division) path += `&division=${encodeURIComponent(division)}`;

  const response = await get({
    apiName: 'admin-docs',
    path,
  }).response;
  const json = (await response.body.json()) as any;
  return {
    items: (json.items ?? []).map((a: any) => mapFromResponse(a)),
    lastKey: json.lastKey,
  };
}

export async function getAdminDoc(adminDocId: string): Promise<AdminDoc> {
  const response = await get({
    apiName: 'admin-docs',
    path: `/admin-docs/${adminDocId}`,
  }).response;
  const json = (await response.body.json()) as any;
  return mapFromResponse(json);
}

export async function createAdminDoc(body: CreateAdminDocRequest): Promise<AdminDoc> {
  const createBody = mapToRequest(body);
  const response = await post({
    apiName: 'admin-docs',
    path: '/admin-docs',
    options: { body: createBody },
  }).response;
  const json = (await response.body.json()) as any;
  return mapFromResponse(json);
}

export async function updateAdminDoc(doc: AdminDoc): Promise<void> {
  const { id, ...updateData } = doc;
  await put({
    apiName: 'admin-docs',
    path: `/admin-docs/${doc.id}`,
    options: { body: updateData as any },
  }).response;
}

export async function deleteAdminDoc(adminDocId: string): Promise<void> {
  await del({
    apiName: 'admin-docs',
    path: `/admin-docs/${adminDocId}`,
  }).response;
}

/** Get presigned upload URL, file key, and public CloudFront URL via generic files API (context: admin-docs). */
export async function getAdminDocUploadUrl(
  fileName: string,
  adminDocId?: string,
  brand: 'tsf' | 'ccl' = 'ccl'
): Promise<{ uploadUrl: string; fileKey: string; publicUrl: string }> {
  const response = await post({
    apiName: 'files',
    path: '/files/upload-url',
    options: {
      body: {
        fileName,
        context: 'admin-docs',
        entityId: adminDocId || undefined,
        brand,
      } as any,
    },
  }).response;
  const json = (await response.body.json()) as { uploadUrl: string; fileKey: string; publicUrl: string };
  return json;
}

function mapFromResponse(doc: any): AdminDoc {
  const rawStatus = doc.status;
  const status =
    rawStatus === AdminDocStatus.Done || rawStatus === AdminDocStatus.Open
      ? rawStatus
      : AdminDocStatus.Open;
  const res = {
    ...doc,
    documents: doc.documents ?? [],
    status,
  };
  return replaceNullsWithEmpty(res) as AdminDoc;
}

function mapToRequest(body: CreateAdminDocRequest): any {
  const parsed = CreateAdminDocRequestSchema.safeParse(body);
  if (parsed.success) return parsed.data;
  throw new Error('Validation error');
}
